import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../lib/api.js';

// API paths
const API_PATHS = {
  GOALS: '/goals',
};

// Thunk for fetching all goals
export const fetchGoals = createAsyncThunk('goals/fetchGoals', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(API_PATHS.GOALS);

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch goals');
  }
});

// Thunk for creating a new goal
export const createGoal = createAsyncThunk('goals/createGoal', async (goalData, { rejectWithValue }) => {
  try {
    const response = await api.post(API_PATHS.GOALS, goalData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create goal');
  }
});

// Thunk for updating goal progress
export const updateGoalProgress = createAsyncThunk('goals/updateGoalProgress', async ({ id, progress }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`${API_PATHS.GOALS}/${id}/progress`, { progress });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update goal progress');
  }
});

// thunk for update goal
export const updateGoal = createAsyncThunk('goals/updateGoal', async ({ id, goal }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`${API_PATHS.GOALS}/${id}`, { ...goal });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update goal');
  }
});

// Thunk for deleting a goal
export const deleteGoal = createAsyncThunk('goals/deleteGoal', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`${API_PATHS.GOALS}/${id}`);
    return id; // Return the id of the deleted goal
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete goal');
  }
});

// Initial state
const initialState = {
  goals: { data: [] },
  loading: 'idle', // 'idle' | 'pending' | 'succeeded' | 'failed'
  error: null,
};

// Create the slice
const goalSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    // Add any additional synchronous reducers here
  },
  extraReducers: (builder) => {
    // Handle fetchGoals
    builder
      .addCase(fetchGoals.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(fetchGoals.fulfilled, (state, action) => {
        state.goals = action.payload;
        state.loading = 'succeeded';
      })
      .addCase(fetchGoals.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })

      // Handle createGoal
      .addCase(createGoal.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(createGoal.fulfilled, (state, action) => {
        state.goals.data.push(action.payload);
        state.loading = 'succeeded';
      })
      .addCase(createGoal.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })

      // Handle updateGoalProgress
      .addCase(updateGoalProgress.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(updateGoalProgress.fulfilled, (state, action) => {
        const index = state.goals.data.findIndex((goal) => goal.id === action.payload.id);
        if (index !== -1) {
          state.goals.data[index] = action.payload;
        }
        state.loading = 'succeeded';
      })
      .addCase(updateGoalProgress.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })

      // Handle deleteGoal
      .addCase(deleteGoal.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(deleteGoal.fulfilled, (state, action) => {
        state.goals = state.goals.filter((goal) => goal.id !== action.payload);
        state.loading = 'succeeded';
      })
      .addCase(deleteGoal.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      });
  },
});
// Export selectors
export const selectAllGoals = (state) => state.goals.goals;
export const selectGoalsLoading = (state) => state.goals.loading === 'pending';
export const selectGoalsError = (state) => state.goals.error;

// Export reducer
export default goalSlice.reducer;
